import { message } from "antd";
import html2canvas from "html2canvas";
import JsPDF from "jspdf";
import { getContrastColor, randomColor } from "./ColorHelper";

export const getFileExtension = (filename: string) => {
  const nameArray = filename.split(".").reverse();

  return filename ? nameArray[0] : "";
};

export const downloadPdf = ({
  noDownload = false,
  elementName = "invoice",
  fileName = "Invoice.pdf",
}: {
  noDownload?: boolean;
  elementName?: string;
  fileName?: string;
}) => {
  const input = document.getElementById(elementName);
  input.classList.add("print-process");
  const result = html2canvas(input as HTMLElement, {
    allowTaint: true,
    useCORS: true,
    scale: 2,
  }).then((canvas) => {
    input.classList.remove("print-process");

    const doc = new JsPDF("p", "mm", "a4");
    const imgWidth = 200;
    const pageHeight = 290;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 5;

    const img = canvas.toDataURL("image/png", 1.0);

    doc.addImage(img, "PNG", 3, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(img, "PNG", 3, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    if (!noDownload) doc.save(fileName);
    return doc.output("blob");
  });

  return result;
};
export const downloadFile = (fileUrl: string) => {
  const url = fileUrl;
  const e = document.createElement("a");
  e.href = url;
  e.target = "_blank";
  e.download = url.substr(url.lastIndexOf("/") + 1);
  document.body.appendChild(e);
  e.click();
  document.body.removeChild(e);
};

export const showFileValidation = (file: any) => {
  if (file) return true;
  message.warning("Please select a valid File.");
};

export const fileToBase64 = async (file: File): Promise<string> => {
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
  });
};

export const urlToBase64 = async (url: string): Promise<string> => {
  try {
    if (!url || typeof url !== "string") {
      console.warn("Invalid URL provided to urlToBase64:", url);
      return "";
    }

    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "image/*",
      },
    });

    if (!response.ok) {
      console.error(
        "Failed to fetch URL:",
        response.status,
        response.statusText,
      );
      return "";
    }

    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.startsWith("image/")) {
      console.error(
        "URL does not point to an image. Content-Type:",
        contentType,
      );
      return "";
    }

    const blob = await response.blob();

    if (blob.size === 0) {
      console.error("Empty blob received from URL");
      return "";
    }

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => {
        console.error("Error reading blob as data URL");
        resolve("");
      };
    });
  } catch (error) {
    console.error("Error converting URL to base64:", error);
    return "";
  }
};

export const generateLogoFromText = (text: string): string => {
  if (!text || text.trim().length === 0) {
    return "";
  }

  const canvas = document.createElement("canvas");
  const size = 200; // Logo size
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "";
  }

  // Get initials (first 1-2 letters)
  const words = text.trim().split(/\s+/);
  let initials = "";
  if (words.length >= 2) {
    // If there are 2+ words, take first letter of first two words
    initials = (words[0][0] + words[words.length - 1][0]).toUpperCase();
  } else {
    // If single word, take first 2 letters or first letter
    initials = text.substring(0, 1).toUpperCase();
  }

  const backgroundColor = randomColor();
  const textColor = getContrastColor(backgroundColor);
  const fontFamilyName = "Noto Sans";

  // Draw background square
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, size, size);

  // Draw text
  ctx.fillStyle = textColor;
  ctx.font = `bold ${size / 1.5}px '${fontFamilyName}', sans-serif`;
  ctx.lineWidth = 2;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(initials, size / 2, size / 2 + 11, size);

  return canvas.toDataURL("image/png");
};
